import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createClientAddressesApi } from './api';
import type {
  CepLookupResult,
  ClientAddressDto,
  ClientAddressInput,
  ClientAddressListItem,
} from './types';

export function useClientAddressesHooks(clientId: number): CrudHooks<
  ClientAddressListItem,
  ClientAddressDto,
  ClientAddressInput
> & {
  api: CrudApi<ClientAddressListItem, ClientAddressDto, ClientAddressInput> & {
    lookupCep: (cep: string) => Promise<CepLookupResult>;
  };
  useLookupCep: () => UseMutationResult<CepLookupResult, Error, string>;
} {
  const addressesApi = useMemo(() => createClientAddressesApi(clientId), [clientId]);
  const queryKey = useMemo(() => ['clients', clientId, 'addresses'] as const, [clientId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<ClientAddressListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => addressesApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: number | null | undefined): UseQueryResult<ClientAddressDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => addressesApi.get(id as number),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<ClientAddressDto, Error, ClientAddressInput> =>
    useMutation({
      mutationFn: (input) => addressesApi.create(input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useUpdate = (): UseMutationResult<
    ClientAddressDto,
    Error,
    { id: number; input: ClientAddressInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => addressesApi.update(id, input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useDelete = (): UseMutationResult<void, Error, number> =>
    useMutation({
      mutationFn: (id) => addressesApi.remove(id),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useLookupCep = (): UseMutationResult<CepLookupResult, Error, string> =>
    useMutation({
      mutationFn: (cep) => addressesApi.lookupCep(cep),
    });

  return { api: addressesApi, useList, useDetail, useCreate, useUpdate, useDelete, useLookupCep };
}
